// Lingua dell'interfaccia (D94, docs/17). Testi da vue-i18n: il dizionario arriva dal backend (GET /traduzioni/{lingua},
// base del programma più le revisioni del traduttore, con il ripiego inglese e italiano già applicato); quello italiano
// di base è anche nel pacchetto, così il CRM resta leggibile se il server non risponde. Con la lingua cambiano anche il
// pacchetto di Quasar (calendario, tabelle) e la formattazione di date e numeri (formato.ts).
import { Quasar } from 'quasar';
import { ref } from 'vue';
import { createI18n } from 'vue-i18n';
import italiano from '../../lingue/it.json';
import LINGUE from '../../lingue/lingue.json';
import { api, esegui } from './api/client';

type Dizionario = typeof italiano;
declare module 'vue-i18n' {
  // Le chiavi si controllano in compilazione sul dizionario italiano, il riferimento
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  export interface DefineLocaleMessage extends Dizionario {}
}

export interface Lingua { codice: string; nome: string; quasar: string; formato: string }
export const LINGUE_DISPONIBILI: Lingua[] = LINGUE;
const RIFERIMENTO = 'it';
const RIPIEGO = 'en';

export const i18n = createI18n({
  legacy: false,
  locale: RIFERIMENTO,
  fallbackLocale: RIFERIMENTO,
  messages: { [RIFERIMENTO]: italiano } as Record<string, Dizionario>,
  missingWarn: import.meta.env.DEV,
  fallbackWarn: false,
});

/** Testo nella lingua in uso, per i moduli che non sono componenti (nei componenti: `const { t } = useI18n()`). */
export const tr: typeof i18n.global.t = ((...a: Parameters<typeof i18n.global.t>) => i18n.global.t(...a)) as typeof i18n.global.t;

/** Lingua in uso (codice). */
export const linguaAttiva = ref(RIFERIMENTO);

/** Locale per Intl (date, numeri): quello della lingua in uso. */
export function localeFormato(): string {
  return LINGUE_DISPONIBILI.find((l) => l.codice === linguaAttiva.value)?.formato ?? 'it-IT';
}

// Pacchetti di Quasar delle lingue disponibili: elenco esplicito, perché Vite deve conoscerli in compilazione
const PACCHETTI_QUASAR: Record<string, () => Promise<{ default: Parameters<typeof Quasar.lang.set>[0] }>> = {
  it: () => import('quasar/lang/it'),
  'en-GB': () => import('quasar/lang/en-GB'),
};

const CHIAVE_BROWSER = 'crm-lingua'; // scelta fatta prima dell'accesso, su questo browser

/** Lingua prima dell'accesso: quella scelta su questo browser, poi quella del browser se c'è, poi l'inglese (non italiani). */
export function linguaIniziale(): string {
  try {
    const scelta = localStorage.getItem(CHIAVE_BROWSER);
    if (scelta && LINGUE_DISPONIBILI.some((l) => l.codice === scelta)) return scelta;
  } catch { /* archiviazione del browser non disponibile */ }
  for (const l of navigator.languages ?? [navigator.language]) {
    const codice = l.toLowerCase();
    const trovata = LINGUE_DISPONIBILI.find((x) => x.codice === codice) ?? LINGUE_DISPONIBILI.find((x) => x.codice === codice.slice(0, 2));
    if (trovata) return trovata.codice;
  }
  return LINGUE_DISPONIBILI.some((l) => l.codice === RIPIEGO) ? RIPIEGO : RIFERIMENTO;
}

/** Ricorda la lingua scelta sulla pagina di accesso (preferenza del browser, non un dato). */
export function ricordaLinguaBrowser(codice: string): void {
  try {
    localStorage.setItem(CHIAVE_BROWSER, codice);
  } catch { /* pazienza */ }
}

// Caricamento in corso: chi chiede la stessa lingua (sessione e router insieme) aspetta lo stesso
let inCorso: { codice: string; promessa: Promise<void> } | null = null;

/** Carica il dizionario della lingua e la applica a testi, Quasar, date e numeri. */
export function impostaLingua(codice: string): Promise<void> {
  if (inCorso?.codice === codice) return inCorso.promessa;
  const promessa = caricaLingua(codice).finally(() => { if (inCorso?.promessa === promessa) inCorso = null; });
  inCorso = { codice, promessa };
  return promessa;
}

async function caricaLingua(codice: string): Promise<void> {
  const lingua = LINGUE_DISPONIBILI.find((l) => l.codice === codice) ?? LINGUE_DISPONIBILI.find((l) => l.codice === RIFERIMENTO)!;
  try {
    const dizionario = await esegui(api.GET('/traduzioni/{lingua}', { params: { path: { lingua: lingua.codice } } }), { ignora401: true });
    i18n.global.setLocaleMessage(lingua.codice, dizionario as Dizionario);
  } catch {
    // Server non raggiungibile: resta il dizionario italiano del pacchetto (o quello già caricato)
    if (!i18n.global.availableLocales.includes(lingua.codice)) return;
  }
  i18n.global.locale.value = lingua.codice;
  linguaAttiva.value = lingua.codice;
  document.documentElement.lang = lingua.codice;
  const pacchetto = await PACCHETTI_QUASAR[lingua.quasar]?.().catch(() => null);
  if (pacchetto) Quasar.lang.set(pacchetto.default);
}
